import React, {
    useEffect, useState, useMemo, useCallback, useContext 
} from 'react';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';
import { MetadataContent } from '@jutro/uiconfig';
import {
    Icon, Loader, InfoLabel, Link, FormattedDateRange, Accordion, AccordionCard, InlineNotification
} from '@jutro/components';
import { AccountBillingDetailsService } from 'gw-capability-billing';
import { UsersProfileDetailsService } from 'gw-capability-profileinfo';
import { PolicyService } from 'gw-capability-policy';
import { AMPSubmissionDraftService } from 'gw-capability-quoteandbind';
import { DocumentService } from 'gw-capability-policydocument';
import { Currency as CurrencyField } from 'gw-components-platform-react';
import { ButtonLink } from '@jutro/router';
import { getNormalizedLOBName, isCapabilityEnabled, hasPermission_RI } from 'gw-portals-config-js';
import { useAuthentication } from 'gw-digital-auth-react';
import { LobIconUtil, LocalDateUtil } from 'gw-portals-util-js';
import { generateURL } from 'gw-portals-url-js';
import { useTranslator, IntlContext } from '@jutro/locale';

// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';

import styles from './LandingPage.module.scss';
import metadata from './LandingPage.metadata.json5';
import messages from './LandingPage.messages';
import { createFormattedDateTimePartsComponent } from 'react-intl/src/components/createFormattedComponent';

const getCellType = (rowData) => {
    const activePeriod = _.first(rowData.periods);
    const lineOfBusiness = activePeriod.lines.length > 1
        ? activePeriod.lines.map((line) => line) : _.first(activePeriod.lines);
    const icon = activePeriod.lines.length > 1 ? 'mi-unarchive' : LobIconUtil.getMaterialIcon(lineOfBusiness);
    return <Icon icon={icon} title={lineOfBusiness} key={lineOfBusiness} />;
};

const isPolicyActive = (periods) => {
    const currentRenewed = periods.length > 1;
    const activePeriod = _.first(periods);
    return new Date(activePeriod.effective).getTime() < new Date().getTime() && !currentRenewed;
};
const pullPolicyStatus = (periods) => {
    // const currentRenewed = periods.length > 1;
    const activePeriod = _.first(periods);
    return activePeriod.displayStatus;
};

const shouldRenewalNotificationShow = (accountPolicyData) => {
    return accountPolicyData.some((policy) => policy.periods.length > 1);
};

const getCellStatus = (rowData) => {
    if (isPolicyActive(rowData.periods)) {
        return <InfoLabel type="success" size="medium" message={messages.statusActive} />;
    }

    return <InfoLabel type="info" size="medium" message={messages.statusRenewed} />;
};

const getCellPolicy = (rowData) => {
    const activePeriod = _.first(rowData.periods);
    const periodWithIDCard = rowData.periods.find((policy) => policy.idCardPublicID);
    return (
        <Link
            className={styles.link}
            to={{
                pathname: `/ri-policies/${activePeriod.policyId}/summary`,
                productCode: activePeriod.lines[0],
                idCard: periodWithIDCard ? {
                    idCardSessionID: _.get(periodWithIDCard, 'idCardSessionID'),
                    idCardPublicID: _.get(periodWithIDCard, 'idCardPublicID')
                } : null
            }}
        >
            {activePeriod.policyId}
        </Link>
    );
};

const getPayrollReportingPeriod = (rowData, rowIndex) => {
    const activePeriod = rowData;
    return (
        <span className={styles.riDataRange}>
            { <FormattedDateRange
                id={`reportPeriod_${rowIndex}`}
                value={activePeriod}
                startDate={new Date(activePeriod.auditEffectiveDate)}
                endDate={new Date(activePeriod.auditExpirationDate)}
                hideLabel
                displayFormat="short"
            /> }
        </span>
    );
};

const getCellEffective = (rowData, rowIndex) => {
    const activePeriod = _.first(rowData.periods);

    return (
        <FormattedDateRange
            id={`effectiveDate_${rowIndex}`}
            value={activePeriod}
            startDate={new Date(activePeriod.effective)}
            endDate={new Date(activePeriod.expiration)}
            hideLabel
            displayFormat="short"
        />
    );
};

const getCellClaim = (rowData) => {
    const activePeriod = _.first(rowData.periods);

    const redirectPath = {
        pathname: '/fnol-select-policy',
        state: {
            policyNumber: activePeriod.policyId,
            redirectPath: '/home',
            claimData: { lossDate: new Date() }
        }
    };
    return (
        <Link to={redirectPath} className={styles.link}>
            {messages.fileClaim}
        </Link>
    );
};

const getCellPremium = (rowData, rowIndex) => {
    const activePeriod = _.first(rowData.periods);
    return (
        <CurrencyField
            id={`premium_${rowIndex}`}
            value={activePeriod.premium}
            dataType="object"
            readOnly
            hideLabel
        />
    );
};

const getContactsData = () => {
    return {
        Billing: {
            dept: 'Billing',
            phone: '800-821-4520 Option 2',
            email: 'billing@ri-net.com'
        },
        Audit: {
            dept: 'Premium Audit',
            phone: '800-662-1485 Option 3',
            email: 'republicaudit@ri-net.com'
        },
        LossControl: {
            dept: 'Loss Control',
            phone: '800-662-1485 Option 8',
            email: 'ricalc@ri-net.com'
        },
        Support: {
            dept: 'Website Support/Other Inquires',
            phone: '800-821-4520 Option 3',
            email: 'ricacustomercare@ri-net.com'
        },
        Claims: {
            dept: 'Claims',
            phone: '888-336-7569',
            fax: '818-789-7286',
            email: 'riclaims@ri-net.com'
        }
    };
};

function LandingPage(props) {
    const translator = useTranslator();
    const intl = useContext(IntlContext);
    const [accountBillingData, setAccountBillingData] = useState([]);
    const [accountPolicyData, setAccountPolicyData] = useState([]);
    const { authHeader } = useAuthentication();
    const auth = useAuthentication();
    const [accountData, setAccountData] = useState([]);
    const [isLoading, setLoadingState] = useState(false);
    const { capabilitiesConfig } = appConfig;
    const [openPayroll, setOpenPayroll] = useState([]);
    const [riContacts, setRiContacts] = useState(() => getContactsData());
    const [isDirectBill, setDirectBill] = useState(auth.authUserData.isDirectBill);
    const [policyHolderName, setPolicyHolderName] = useState();
    const [policyNumber, setPolicyNumber] = useState('');
    const [policyStatus, setPolicyStatus] = useState('');
    const [billingAvailable, setBillingAvailable] = useState(true);
    const [hasPriorBalance, setHasPriorBalance] = useState(false);
    const [hasPAYGO, setHasPAYG] = useState(false);
    const [hasGaPolicy, setHasGaPolicy] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState({});
    const [showNotification, setShowNotification] = useState(true);

    useEffect(() => {
        if (showNotification) {
            const timer = setTimeout(() => setShowNotification(false), 60000);
            return () => clearTimeout(timer);
        }
    }, [showNotification]);

    useEffect(() => {
        setLoadingState(true);
        let billingPromise = Promise.resolve([]);
        const { authUserData } = auth;
        if (isCapabilityEnabled({ capabilitiesConfig, capabilityName: 'billing' }) && (hasPermission_RI({ authUserData, permissionName: 'hasAccountingAccess' }))) {
            billingPromise = AccountBillingDetailsService.getAccountBillingSummary(authHeader).catch((err) => {
                setBillingAvailable(false);
                return Promise.resolve([]);
            });
        }
        Promise.all(
            [
                billingPromise.then(setAccountBillingData),
                PolicyService.getOpenPayrollsExt(authHeader).then(setOpenPayroll),
                PolicyService.getAccountPolicySummaries(authHeader).then(setAccountPolicyData),
                UsersProfileDetailsService.getAccountsContactSummaries(authHeader).then(setAccountData)
            ]
        ).finally(() => {
            setLoadingState(false);
        });
        // disable re render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setPolicyDataConst(accountPolicyData);
    }, [accountPolicyData]);
    
    useEffect(() => {       
        formatBillingDates();// FB 17384

        const amount = _.get(accountBillingData.priorAccountBalance_Ext, 'amount');
        setHasPriorBalance(!_.isUndefined(amount) && amount !== 0);
        if (accountBillingData.paymentMethod_Ext !== undefined) {
            parsePaymentMethod(accountBillingData.paymentMethod_Ext);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountBillingData]);

    const aggregateBilling = useCallback((invoices) => {
        if (invoices.length === 0) {
            return null;
        }

        let total = 0;
        let due = new Date(invoices[0].dueDate);

        _.each(invoices, (invoice) => {
            total += invoice.amountDue.amount;
            const newDue = new Date(invoice.dueDate);
            if (newDue.getTime() < due.getTime()) {
                due = newDue;
            }
        });

        return {
            amountDue: {
                amount: total,
                currency: invoices[0].amountDue.currency
            },
            earliestDue: due
        };
    }, []);

    const getMyBalanceOverDueAndCurrentData = useCallback(
        (invoiceStatus) => {
            if (accountBillingData.activeInvoices) {
                const invoices = accountBillingData.activeInvoices;
                return aggregateBilling(
                    invoices.filter((invoice) => invoice.invoiceStatus === invoiceStatus)
                );
            }
            return null;
        },
        [accountBillingData.activeInvoices, aggregateBilling]
    );

    // FB 15946 ---
    const parsePaymentMethod = (paymentMethod_Ext) => {
        const paymentMethod = {};
        if (paymentMethod_Ext.includes('Standard')) {
            paymentMethod.method = 'Standard';
            paymentMethod.recurring = 'No';
        } else {
            const paymentArray = paymentMethod_Ext.split(' ');
            paymentArray.shift();
            paymentMethod.method = paymentArray.join(' ');
            paymentMethod.recurring = paymentMethod_Ext.includes('Recurring') ? 'Yes' : 'No';
        }

        setPaymentMethod(paymentMethod);
    };
    // --- FB 15946

    // JZ Only array can be using .find
    const setPolicyDataConst = (accountPolicyData) => {
        accountPolicyData.find((policyData) => { 
            policyData.periods.find((period) => { 
                if (period.policyId) { 
                    setPolicyNumber(period.policyId);
                    setPolicyHolderName(period.displayNameExt);
                    setPolicyStatus(period.displayStatus);
                    setHasPAYG(period.hasPayAsYouGoExt);
                    setHasGaPolicy(period.hasGAPolicyExt);
                }
            });
        });
    };

    const homeHeaderContent = useMemo(() => {
        let activePeriod = null;
        let billingNumber = null;
        let billingStatus = null;
        if (!_.isEmpty(accountPolicyData)) {
            accountPolicyData.find((policyData) => {
                policyData.periods.find((period) => {
                    activePeriod = period;
                    if (period.billingIdExt) {
                        billingNumber = period.billingIdExt;
                    }
                });
            });
        }
        billingStatus = _.get(accountBillingData, 'status_Ext');
        const statusClassName = billingStatus === 'Delinquent' ? styles.riInfoStatusAlert : styles.gwInfoStatus;

        const now = new Date();
        const hideSecondNotification = now > new Date('2025-11-1');
        const hideCreditCardNotification = now > new Date('2026-1-30');//FB 23050

        return (
            <div><br/>
                <div className={styles.riSubTitle}>
                    <div>
                        Account #: &nbsp;
                        <span>
                            <Link to={`/ri-billing/${policyNumber}/summary`} className={styles.riLinkDetails}>
                                {billingNumber}
                            </Link>
                        </span>
                        <InfoLabel
                            id="billingStatus"
                            size="medium"
                            className={statusClassName}
                            type="info"
                            message={billingStatus}
                        />
                    </div>
                    <div className={styles.riSubTitle}>
                        Policy #: &nbsp;
                        <span>
                            <Link to={`/ri-policies/${policyNumber}/summary`} className={styles.riLinkDetails}>
                                {policyNumber}
                            </Link>
                        </span>
                        <span className={styles.riEffDateGroup}>
                            (
                            { <FormattedDateRange
                                id="effectiveDate_1"
                                value={activePeriod}
                                startDate={new Date(activePeriod?.effective)}
                                endDate={new Date(activePeriod?.expiration)}
                                hideLabel
                                displayFormat="short"
                            /> }
                            )
                        </span>
                        <InfoLabel
                            id="policyStatus"
                            size="medium"
                            className={styles.gwInfoStatus}
                            type="info"
                            message={policyStatus}
                        />
                    </div>
                </div>
                <div className={styles.riTypeWithLOB}>
                    <div>
                        <Icon icon="domain" className={styles.syncIcon} />
                    </div>
                    <div>
                        <h2>{policyHolderName}</h2>
                    </div>
                </div>
                {showNotification && !hideCreditCardNotification && (
                    <div className={styles.riNotification} style={{ width: '100%', boxSizing: 'border-box' }}>
                        <div style={{ background: '#eaf6fb', border: '1px solid #b3e0f7', borderRadius: 4, padding: 16, color: '#1a3c4e', width: '100%', maxWidth: 'none' }}>
                            <strong style={{ display: 'block', marginBottom: 8 }}>
                                Effective January 29, 2026, Republic Indemnity will transition card processing
                            </strong>
                            A 2.99% processing fee will apply to all card transactions <b>by an authorized third-party service provider</b> beginning January 29, 2026. For a no-fee option,  you can select to pay via ACH from a checking account or mail in the payment.
                        </div>
                    </div>
                )}
                {showNotification && !hideSecondNotification && (
                    <div className={styles.riNotification} style={{ width: '100%', boxSizing: 'border-box' }}>
                        <div style={{ background: '#eaf6fb', border: '1px solid #b3e0f7', borderRadius: 4, padding: 16, color: '#1a3c4e', width: '100%', maxWidth: 'none' }}>
                            <strong style={{ display: 'block', marginBottom: 8 }}>
                                New Login Step Starting November 1, 2025
                            </strong>
                            To enhance security, Multi-Factor Authentication (MFA) will be required. You’ll verify your identity using a code sent to your email when logging into the portal. Make sure your email address is up to date. Shared logins must request individual access. Questions? Contact <a href='mailto:RICACustomerCare@ri-net.com' target='_blank'>RICACustomerCare@ri-net.com</a>.  
                            Click{' '}
                            <Link to="/editprofile" style={{ color: '#0073e6', textDecoration: 'underline' }}>
                                here
                            </Link>{' '}to update your profile.
                        </div>
                    </div>
                )}
            </div>
        );
    }, [accountPolicyData, accountBillingData, policyNumber, policyHolderName, policyStatus, showNotification]);

    const payrollTitleContent = useMemo(() => {
        return (
            <div className={styles.riPayrollTitle}>
                <div className={styles.riFloatingLeft}>
                    <h2 className="gwPoliciesTableTitle">{translator(messages.riOpenPayrollTableTitle)}</h2>
                </div>

                <div className={styles.riFloatingRight}>
                    <ButtonLink to={`/ri-policies/${policyNumber}/audits`}>
                        {translator(messages.riAuditStatementInTitle)}
                    </ButtonLink>
                </div>
            </div>
        );
    }, [policyNumber, translator]);

    const getCellStatus = (rowData) => {
        return <InfoLabel type="info" size="medium" className={styles.riInfoLabel} message={pullPolicyStatus(rowData.periods)} />;
    };

    const readValue = useCallback(
        (id, path) => {
            return _.get(accountBillingData, path);
        },
        [accountBillingData]
    );

    const getBalanceData = useCallback(() => {
        const overdue = getMyBalanceOverDueAndCurrentData('due');
        const current = getMyBalanceOverDueAndCurrentData('billed');
        if (overdue && current) {
            return {
                amount: current.amountDue.amount + overdue.amountDue.amount,
                currency: current.amountDue.currency
            };
        }
        if (overdue || current) {
            return (overdue || current).amountDue;
        }
        return null;
    }, [getMyBalanceOverDueAndCurrentData]);

    // const readValue = useCallback(
    //     (id, path) => {
    //         if (path === 'balanceAmount') {
    //             return getBalanceData();
    //         }
    //         if (path === 'overDueAmount') {
    //             return _.get(getMyBalanceOverDueAndCurrentData('due'), 'amountDue');
    //         }
    //         if (path === 'currentAmount') {
    //             return _.get(getMyBalanceOverDueAndCurrentData('billed'), 'amountDue');
    //         }
    //         if (path === 'dueDate') {
    //             return _.get(getMyBalanceOverDueAndCurrentData('due'), 'earliestDue');
    //         }
    //         return _.get(accountBillingData, path);
    //     },
    //     [accountBillingData, getBalanceData, getMyBalanceOverDueAndCurrentData]
    // );

    const policyWithIDCard = useMemo(() => {
        let periodWithIDCard;
        accountPolicyData.some((policyData) => {
            return policyData.periods.some((period) => {
                if (period.idCardPublicID) {
                    periodWithIDCard = period;
                    return true;
                }
                return false;
            });
        });
        return periodWithIDCard;
    }, [accountPolicyData]);

    const getIDCardQuickLink = useMemo(() => {
        if (!policyWithIDCard) {
            return null;
        }
        return DocumentService.downloadPolicyDocument(
            policyWithIDCard.idCardPublicID,
            policyWithIDCard.idCardSessionID
        );
    }, [policyWithIDCard]);

    const automatedPaymentURL = useMemo(() => {
        const billingID = _.get(accountBillingData, 'manualPayPaymentGroups[0]');

        if (!billingID) {
            return undefined;
        }
        return `billing-summary/${billingID}`;
    }, [accountBillingData]);

    const firstAccountSummaryDefined = useMemo(() => {
        return accountData.length <= 1 && !_.isEmpty(accountData);
    }, [accountData]);

    const mailToTrigger = useCallback((event, email) => {
        event.preventDefault();
        window.location = `mailto:${email}`;
    }, []);

    const phoneCallTrigger = useCallback((event, phoneNumber) => {
        event.preventDefault();
        window.location = `tel:${phoneNumber}`;
    }, []);

    const claimCapabilityCheck = useMemo(() => {
        const { authUserData } = auth;
        return (isCapabilityEnabled({ capabilitiesConfig, capabilityName: 'fnol' }) && hasPermission_RI({ authUserData, permissionName: 'hasClaimsAccess' }));
    }, []);

    const onQuoteQuickLinkClick = useCallback(
        (lob) => () => {
            const accountSummary = accountData[0];
            const submission = {
                baseData: {
                    productCode: lob,
                    accountHolder: accountSummary,
                    accountNumber: accountSummary.accountNumber,
                    policyAddress: accountSummary.accountContact.primaryAddress,
                    periodStartDate: LocalDateUtil.today()
                }
            };
            AMPSubmissionDraftService.createForAccount(submission, authHeader).then(
                (savedSubmission) => {
                    const { url } = appConfig.quoteAndBuy;
                    const location = `${url}/quote-${getNormalizedLOBName(lob)}`;
                    const params = {
                        quoteID: savedSubmission.quoteID,
                        postalCode: savedSubmission.baseData.policyAddress.postalCode
                    };

                    window.location = generateURL(location, params);
                }
            );
        },
        [accountData, authHeader]
    );

    const getCellAgent = useCallback(
        (rowData) => {
            return (
                <>
                    <Link className={styles.link} onClick={(e) => mailToTrigger(e, rowData.producerContact.email)} href="/">
                        {rowData.producerContact.displayName}
                    </Link>
                    <br />
                    {rowData.producerContact.phoneNumber ? (
                        <span>
                            {translator(messages.policyDetailsTel)}
                            {rowData.producerContact.phoneNumber}
                        </span>
                    ) : (
                        ''
                    )}
                </>
            );
        },
        [mailToTrigger, translator]
    );

    const getTableOverrides = useCallback(() => {
        return {
            typeColumn: { renderCell: getCellType },
            statusColumn: { renderCell: getCellStatus },
            policyColumn: { renderCell: getCellPolicy },
            effectiveColumn: { renderCell: getCellEffective },
            premiumColumn: { renderCell: getCellPremium },
            fileAClaimColumn: {
                visible: false,
                renderCell: getCellClaim
            },
            agentColumn: {
                visible: accountData.length > 1,
                renderCell: getCellAgent
            }
        };
    }, [accountData, getCellAgent]);

    const onFileAClaimQuickLinkClick = useCallback(
        () => () => {
            const { history } = props;
            return history.push({
                pathname: '/fnol-select-policy',
                state: {
                    redirectPath: '/home',
                    claimData: { lossDate: new Date() }
                }
            });
        },
        [props]
    );

    const handleLinkClick = useCallback(
        (event) => {
            const { history } = props;
            return history.push(event.path);
        },
        [props]
    );

    const handleWhatsNewLinkClick = useCallback(() => {

    });

    // FB 21686  --- 
    const onPayrollLinkClick = useCallback(
        (event) => {
            event.preventDefault();
            
            const { history } = props;
            const nextPath = '/ri-payroll';
            if (policyNumber === undefined || policyNumber === '') {
                return '';
            }
            return history.push({
                pathname: nextPath,
                redirectPath: '/home',
                state: {
                    policyNumber: policyNumber
                }
            });
        },
        [props, policyNumber]
    );
    // --- FB 21686

    const onLossRunLinkClick = useCallback(
        (event) => {
            event.preventDefault();
            const { history } = props;
            const nextPath = `/ri-policies/${policyNumber}/lossruns`;
            if (policyNumber === undefined || policyNumber === '') {
                return '';
            }
            return history.push({
                pathname: nextPath,
                redirectPath: '/home',
                state: {}
            });
        },
        [props, policyNumber]
    );

    // FB 14847
    const onFormsOrderingClick = useCallback(
        (event) => {
            event.preventDefault();
            const { history } = props;
            const nextPath = '/formsOrdering';

            return history.push({
                pathname: nextPath,
                redirectPath: '/home',
                state: {}
            });
        },
        [props]
    );

    const onNotificationClick = useCallback(
        (event) => {
            event.preventDefault();
            const { history } = props;
            const nextPath = '/ri-notifications';

            return history.push({
                pathname: nextPath,
                redirectPath: '/home',
                state: {}
            });
        },
        [props]
    );
    const showBillingSection = useMemo(() => {
        const { authUserData } = auth;
        return (isCapabilityEnabled({ capabilitiesConfig, capabilityName: 'billing' }) && (hasPermission_RI({ authUserData, permissionName: 'hasAccountingAccess' })) && billingAvailable);
    }, [billingAvailable]);

    const openPayrollsData = useMemo(() => {
        let openPayrollData = [];
        if (!_.isEmpty(openPayroll)) {
            openPayrollData = openPayroll.filter((data) => data.auditMethod !== 'Physical' && data.auditMethod !== 'Phone');
        }

        const sortedData = _.orderBy(openPayrollData, (o) => {
            return o.dueDate;
        }, ['desc']);

        return sortedData;
    }, [openPayroll]);

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    const getEmailField = (emailAddres) => {
        if (emailAddres === undefined || emailAddres === '') {
            return '';
        }
        return (
            <Link className={styles.link} onClick={(e) => mailToTrigger(e, emailAddres)} href="/">
                {emailAddres}
            </Link>
        );
    };

    const getPhoneField = (phone) => {
        const phoneArr = phone.split(' ');
        const phoneNumber = phoneArr[0];
        const option = phoneArr[1] === undefined ? '' : `${phoneArr[1]} ${phoneArr[2]}`;
        return (
            <div>
                <Link className={styles.link} onClick={(e) => phoneCallTrigger(e, phoneNumber)} href="/">
                    {phoneNumber}
                </Link>
                <span className={styles.riPhoneOption}>
                    {' '}
                    {option}
                </span>
            </div>
        );
    };

    // Control the display color by the isDelinquent flag
    const getDelinquentClassName = (isDelinquent, status) => {
        if (isDelinquent || status === 'Delinquent') {
            isDelinquent = true;
        }

        const className = isDelinquent ? 'riTileLabel riTileWarning riTextAlignRight'
            : 'riTileLabel riTextAlignRight';
        return className;
    };

    const getDueDatePath = (accountBillingData) => {
        let isDelinquent = false;
        if (accountBillingData.isDelinquent || accountBillingData.status_Ext === 'Delinquent') {
            isDelinquent = true;
        }
        const path = isDelinquent && accountBillingData.dueDate_Ext ? 'dueDate_Ext' : 'invoiceDueDate_Ext';
        return path;
    };

    const getDelinquentAmountClassName = (isDelinquent, status, amountObj) => {
        const amount = _.get(amountObj, 'amount');
        let isShowingColor = false;

        if (amount !== 0 && (isDelinquent || status === 'Delinquent')) {
            isShowingColor = true;
        }
        const className = isShowingColor ? 'riTileLabel riTileWarning riTextAlignRight'
            : 'riTileLabel riTextAlignRight';
        return className;
    };

    // Open Payroll Reports fileds -------------
    const getCellDueDate1 = (rowData) => {
        const dueDate = intl.formatDate(new Date(rowData.dueDate), { year: 'numeric', month: 'short', day: 'numeric' });
        return (
            <span>{dueDate}</span>
        );
    };

    const getCellServiceLocation = (rowData) => {
        return (
            <span>{rowData.serviceLocationName}</span>
        );
    };
    const getCellReport = (rowData) => {
        const redirectPath = {
            pathname: '/ri-payroll',
            state: {
                policyNumber: rowData.policyNumber, 
                auditNumber: rowData.auditNumber,
                serviceLocNum: rowData.serviceLocationNum,
                redirectPath: '/home'
            }
        };
        return (
            <Link to={redirectPath} className={styles.link}>
                Report Payroll
            </Link>
        );
    };

    const getGaPolicyLink = () => {
        const { url } = appConfig.mediaSidebar.GAPolicy;
        const baseUrl = new URL(url);
        const params = {};
        return generateURL(baseUrl, params);
    };

    const getPayGLink = () => {
        const { url } = appConfig.mediaSidebar.PayAsYouGo;
        const baseUrl = new URL(url);
        const params = {};
        return generateURL(baseUrl, params);
    };

    const getPayAsYouGoLink = () => {
        const { url } = appConfig.mediaSidebar.PayAsYouGo;
        const baseUrl = new URL(url);
        const params = {};
        return generateURL(baseUrl, params);
    };
    // ----------- Open Payroll Reports fileds

    const paymentPageLink = () => {
        const redirectPath = {
            pathname: '/ri-payment',
            state: {
            }
        };
        return (
            <Link to={redirectPath} className={styles.link}>
                {paymentMethod.recurring === 'Yes' ? 'Enrolled' : 'Not Enrolled'}
            </Link>
        );
    };

    const override = {
        '@field': {
            phone: {
                labelPosition: 'left'
            }
        },        
            
        myBalanceOverdueAmount: {
            visible: !_.isEmpty(getMyBalanceOverDueAndCurrentData('due'))
        },
        myBalanceCurrentAmount: {
            visible: !_.isEmpty(getMyBalanceOverDueAndCurrentData('billed'))
        },
        dueDateContainer: {
            visible: !_.isEmpty(getMyBalanceOverDueAndCurrentData('due'))
        },
        dueDateLabel: {
            visible: !_.isEmpty(getMyBalanceOverDueAndCurrentData('billed'))
        },
        payNowButton: {
            visible: firstAccountSummaryDefined
        },
        payNowButton2: {
            visible: hasPriorBalance
        },
        mediaSidebar: {
            showContact: accountData.length > 1
        },
        policiesTable: {
            data: accountPolicyData
        },
        printAnIDCardLink: {
            visible: !_.isNil(policyWithIDCard),
            href: getIDCardQuickLink,
            target: '_blank'
        },
        setupAutomatedPaymentsLink: {
            to: automatedPaymentURL,
            visible: !!automatedPaymentURL && showBillingSection
        },
        getAPAQuote: {
            onClick: onQuoteQuickLinkClick('PersonalAuto'),
            visible: appConfig.quoteAndBuy.ampQuote && firstAccountSummaryDefined
        },
        getAHOQuote: {
            onClick: onQuoteQuickLinkClick('Homeowners'),
            visible: appConfig.quoteAndBuy.ampQuote && firstAccountSummaryDefined
        },
        renewalNotification: {
            visible: shouldRenewalNotificationShow(accountPolicyData)
        },
        fileAClaimLink: {
            onClick: onFileAClaimQuickLinkClick(),
            visible: claimCapabilityCheck
        },
        payGLink: {
            href: getPayGLink(),
            target: '_blank',
            visible: hasPAYGO
        },       
        gaPolicyLink: {
            href: getGaPolicyLink(),
            visible: hasGaPolicy
        },
        tileGroupContainer: {
            visible: showBillingSection
        },
        makeApaymentPageLink: {
            visible: showBillingSection
        },
        billingNotAvailable: {
            visible: !billingAvailable
        },
        tileWithAdContainer: {
            visible: isDirectBill
        },
        riNotificationLink: {
            visible: isDirectBill
        },
        // Account Summary fields:
        riBalanceCurrentAmount: { className: getDelinquentAmountClassName(accountBillingData.isDelinquent, accountBillingData.status_Ext, accountBillingData.amountDue_Ext) },
        riBalanceDate: {
            className: getDelinquentClassName(accountBillingData.isDelinquent, accountBillingData.status_Ext),
            path: getDueDatePath(accountBillingData)
        },
        riBillingStatus: { className: getDelinquentClassName(accountBillingData.isDelinquent, accountBillingData.status_Ext) },
        riPriorPolicyBalanceAmount: { className: getDelinquentAmountClassName(accountBillingData.isDelinquent, accountBillingData.status_Ext, accountBillingData.priorAmountDue_Ext) },
        riPriorPolicyBalanceDate: { className: getDelinquentClassName(accountBillingData.isDelinquent, accountBillingData.status_Ext) },

        // Open Payroll Reports table:
        openPayrollTable: {
            data: openPayrollsData
        },
        reportingPeriodColumn: { renderCell: getPayrollReportingPeriod },
        dueDateColumn: { renderCell: getCellDueDate1 },
        serviceLocationColumn: { renderCell: getCellServiceLocation },
        reportColumn: { renderCell: getCellReport },

        // Republic Contacts
        
        billingContactPhone: { content: getPhoneField(riContacts.Billing.phone) },
        billingContactEmail: { content: getEmailField(riContacts.Billing.email) },
        
        auditContactPhone: { content: getPhoneField(riContacts.Audit.phone) },
        auditContactEmail: { content: getEmailField(riContacts.Audit.email) },
        
        lossControlContactPhone: { content: getPhoneField(riContacts.LossControl.phone) },
        lossControlContactEmail: { content: getEmailField(riContacts.LossControl.email) },

        supportContactPhone: { content: getPhoneField(riContacts.Support.phone) },
        supportContactEmail: { content: getEmailField(riContacts.Support.email) },

        claimsContactPhone: { content: getPhoneField(riContacts.Claims.phone) },
        claimsContactFax: { content: getPhoneField(riContacts.Claims.fax) },
        claimsContactEmail: { content: getEmailField(riContacts.Claims.email) },

        pageTitle: {
            content: homeHeaderContent
        },
        payrollTitle: {
            content: payrollTitleContent
        },
        riPaymentMethod: {
            visible: false,
            value: paymentMethod.method
        },
        riRecurring: {
            value: paymentPageLink()
        },
        ...getTableOverrides()
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveValue: readValue,
        resolveCallbackMap: {
            handleLinkClick,
            onLossRunLinkClick,
            onFormsOrderingClick,
            onNotificationClick,
            onPayrollLinkClick
        }
    };

    // eslint-disable-next-line max-len
    return (<MetadataContent uiProps={metadata.pageContent} overrideProps={override} {...resolvers} />);

    // FB 17384 -------------------
    function formatBillingDates() {
        // Helper to check if a value needs formatting (not already formatted)
        const needsFormatting = (dateValue) => {
            if (!dateValue) return false;
            // If it contains a dash or 'T', it's likely an ISO date string that needs formatting
            // If it's already formatted like "Jan 15, 2026", it won't have dashes
            return typeof dateValue === 'string' && (dateValue.includes('-') || dateValue.includes('T'));
        };

        let needsUpdate = false;
        const updatedData = { ...accountBillingData };
        
        if (needsFormatting(accountBillingData.dueDate_Ext)) {
            updatedData.dueDate_Ext = 
                intl.formatDate(new Date(accountBillingData.dueDate_Ext), { year: 'numeric', month: 'short', day: 'numeric' });
            needsUpdate = true;
        }
        if (needsFormatting(accountBillingData.priorInvoiceDueDate_Ext)) {
            updatedData.priorInvoiceDueDate_Ext = 
                intl.formatDate(new Date(accountBillingData.priorInvoiceDueDate_Ext), { year: 'numeric', month: 'short', day: 'numeric' });
            needsUpdate = true;
        }
        if (needsFormatting(accountBillingData.invoiceDueDate_Ext)) {
            updatedData.invoiceDueDate_Ext = 
                intl.formatDate(new Date(accountBillingData.invoiceDueDate_Ext), { year: 'numeric', month: 'short', day: 'numeric' });
            needsUpdate = true;
        }
        if (accountBillingData.lastPayment && needsFormatting(accountBillingData.lastPayment.paymentDate)) {
            updatedData.lastPayment = {
                ...accountBillingData.lastPayment,
                paymentDate: intl.formatDate(new Date(accountBillingData.lastPayment.paymentDate), { year: 'numeric', month: 'short', day: 'numeric' })
            };
            needsUpdate = true;
        }
        if (needsUpdate) {
            setAccountBillingData(updatedData);
        }
    };
    // ------------------ FB 17384 
}

export default withRouter(LandingPage);
